import {cn} from '@tempmee/core'
import {OutlineTrashIcon, OutlineUploadIcon} from '@tempmee/react-icons'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {IconButton} from '../icon-button'
import {LinearProgress, type LinearProgressColor} from '../linear-progress'

export type FileUploadStatus = 'uploading' | 'complete' | 'failed'

export interface FileUploadItemProps extends HTMLAttributes<HTMLDivElement> {
  /** File name / title. Coloured red when `status="failed"`. */
  name: ReactNode
  /** Left meta cell (e.g. "100kb", or an error reason like "File too large"). */
  meta?: ReactNode
  /** Upload state — drives the bar color and title color. @default 'uploading' */
  status?: FileUploadStatus
  /**
   * Right meta cell. Defaults to the status copy
   * (`uploading`→"Loading", `complete`→"Upload Complete", `failed`→"Failed").
   * Pass `null` to hide it.
   */
  statusLabel?: ReactNode
  /** Determinate progress, `0`–`100`. `complete` forces 100; omit for indeterminate. */
  value?: number
  /** Renders a trash `IconButton` when provided. */
  onDelete?: () => void
  /** Accessible label for the delete button. @default 'Remove file' */
  deleteLabel?: string
  /** Override the leading icon (rendered at 22px). */
  icon?: ReactNode
}

const STATUS_COLOR: Record<FileUploadStatus, LinearProgressColor> = {
  uploading: 'primary',
  complete: 'success',
  failed: 'error',
}

/** Built-in status copy (overridable via `statusLabel`). */
const STATUS_LABEL: Record<FileUploadStatus, string> = {
  uploading: 'Loading',
  complete: 'Upload Complete',
  failed: 'Failed',
}

/**
 * A single file's upload row: leading icon, file name, a meta · status line, a
 * thin `LinearProgress` bar, and an optional delete button. Presentational — the
 * consumer supplies `status`/`value` and handles `onDelete`.
 */
export const FileUploadItem = forwardRef<HTMLDivElement, FileUploadItemProps>(
  (
    {
      name,
      meta,
      status = 'uploading',
      statusLabel,
      value,
      onDelete,
      deleteLabel = 'Remove file',
      icon = <OutlineUploadIcon />,
      className,
      ...props
    },
    ref,
  ) => {
    const failed = status === 'failed'
    const barValue = status === 'complete' ? 100 : value
    const resolvedStatusLabel = statusLabel === undefined ? STATUS_LABEL[status] : statusLabel
    const hasMeta = meta != null || resolvedStatusLabel != null

    return (
      <div
        ref={ref}
        className={cn('flex w-full items-center gap-4 rounded-2xl p-4', className)}
        {...props}
      >
        <span className="flex size-7 shrink-0 items-center justify-center text-text-primary [&_svg]:size-[22px]">
          {icon}
        </span>

        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              'text-base font-bold leading-7',
              failed ? 'text-error' : 'text-text-primary',
            )}
          >
            {name}
          </span>
          {hasMeta && (
            <span
              className={cn(
                'flex items-center gap-2 text-sm',
                failed ? 'text-error' : 'text-text-secondary',
              )}
            >
              {meta != null && <span>{meta}</span>}
              {meta != null && resolvedStatusLabel != null && <span aria-hidden>•</span>}
              {resolvedStatusLabel != null && <span>{resolvedStatusLabel}</span>}
            </span>
          )}
          <LinearProgress className="mt-1" color={STATUS_COLOR[status]} value={barValue} />
        </div>

        {onDelete && (
          <IconButton
            Icon={<OutlineTrashIcon className="size-6" />}
            color="secondary"
            appearance="default"
            aria-label={deleteLabel}
            onClick={onDelete}
          />
        )}
      </div>
    )
  },
)
FileUploadItem.displayName = 'FileUploadItem'
